import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useJsApiLoader, GoogleMap, MarkerF } from '@react-google-maps/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyMapPlaceholder from '../components/PropertyMapPlaceholder';
import PropertyListingsTabs from '../components/PropertyListingsTabs';
import GuestPhotos from '../components/GuestPhotos';
import { useApi, favorites as favoritesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { recordPropertyView } from '../utils/browsingHistory';
import { GOOGLE_MAPS_LIBRARIES } from '../utils/googleMaps';

function Stars({ rating }) {
  const r = Number(rating);
  const full  = Math.floor(r);
  const half  = r % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span style={{ color: 'var(--warning)', fontSize: 'var(--text-md)', letterSpacing: '0.05em' }}>
      {'★'.repeat(full)}{half ? '½' : ''}{'☆'.repeat(empty)}
    </span>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function PropertyDetail() {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading, error } = useApi(`/properties/${idOrSlug}`);
  const { data: favData, refetch: refetchFavorites } = useApi(user?.role === 'buyer' ? '/favorites' : null);
  const [favoriteBusy, setFavoriteBusy] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (data?.property) recordPropertyView(data.property);
  }, [data?.property]);

  // Canonicalize the URL to the slug — covers old UUID links and stale slugs.
  useEffect(() => {
    if (data?.property?.slug && data.property.slug !== idOrSlug) {
      navigate(`/properties/${data.property.slug}`, { replace: true });
    }
  }, [data?.property, idOrSlug, navigate]);

  const propertyId = data?.property?.id;
  const isFavorited = favData?.favorites?.some(f => f.property_id === propertyId) || false;

  async function toggleFavorite() {
    if (!propertyId) return;
    setFavoriteBusy(true);
    try {
      if (isFavorited) await favoritesApi.remove(propertyId);
      else await favoritesApi.add(propertyId);
      refetchFavorites();
    } catch {
      // no-op — non-critical action
    } finally {
      setFavoriteBusy(false);
    }
  }

  if (loading) return <div className="page-loading">Loading property…</div>;
  if (error || !data?.property) {
    return (
      <div className="page">
        <Navbar />
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <h2>Property not found</h2>
          <Link to="/search" style={{ color: 'var(--brand)' }}>← Back to search</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const property = data.property;
  const listings = data.listings || [];
  const heroImg  = property.image_urls?.[0];
  const hasCoords = property.latitude && property.longitude;
  const mapCenter = hasCoords
    ? { lat: parseFloat(property.latitude), lng: parseFloat(property.longitude) }
    : null;

  return (
    <div className="page">
      <Navbar />

      {/* ── 1. Hero ──────────────────────────────────────────────────────── */}
      {heroImg ? (
        <div style={{ width: '100%', height: 'clamp(300px, 45vw, 420px)', overflow: 'hidden' }}>
          <img src={heroImg} alt={property.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      ) : (
        <div style={{ width: '100%', height: 'clamp(300px, 40vw, 380px)', background: 'linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%)' }} />
      )}

      <div className="container" style={{ padding: '2rem 1rem 3rem' }}>

        {/* ── Info bar: name, rating, contact — overlaps hero bottom edge ──── */}
        <div className="property-info-bar">
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 2rem)', fontWeight: 700, marginBottom: '0.3rem' }}>
              {property.name}
            </h1>
            <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-base)', marginBottom: '0.6rem' }}>
              {property.address || property.location}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              {property.rating && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 'var(--text-base)' }}>
                  <Stars rating={property.rating} />
                  <strong>{Number(property.rating).toFixed(1)}</strong>
                  <span style={{ color: 'var(--ink-muted)' }}>
                    ({Number(property.review_count || 0).toLocaleString()} reviews)
                  </span>
                </span>
              )}
              {property.phone && (
                <a href={`tel:${property.phone}`} style={{ fontSize: 'var(--text-base)', color: 'var(--ink-muted)', textDecoration: 'none' }}>
                  {property.phone}
                </a>
              )}
              {property.website && (
                <a
                  href={property.website.startsWith('http') ? property.website : `https://${property.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  {property.website}
                </a>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            {user?.role === 'buyer' && (
              <button
                type="button"
                onClick={toggleFavorite}
                disabled={favoriteBusy}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  background: isFavorited ? 'var(--danger-bg)' : 'var(--surface)',
                  border: `1.5px solid ${isFavorited ? 'var(--danger-bg)' : 'var(--border)'}`,
                  borderRadius: 999, padding: 'var(--space-2) var(--space-4)', minHeight: 38,
                  cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 600,
                  color: isFavorited ? 'var(--danger)' : 'var(--ink-muted)',
                }}
              >
                <span>{isFavorited ? '♥' : '♡'}</span>
                {isFavorited ? 'Saved' : 'Save'}
              </button>
            )}
            <Link to="/search" style={{ fontSize: 'var(--text-sm)', color: 'var(--brand)', whiteSpace: 'nowrap' }}>
              ← Back to search
            </Link>
          </div>
        </div>

        {/* ── 2. About ─────────────────────────────────────────────────────── */}
        <div style={{ marginTop: '2rem', marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: '0.75rem' }}>About</h2>
          <p style={{ color: 'var(--ink)', lineHeight: 1.7, fontSize: 'var(--text-base)', maxWidth: 760 }}>
            {property.description}
          </p>
          {property.amenities?.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amenities</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {property.amenities.map(a => (
                  <span key={a} style={{ background: 'var(--sand)', padding: 'var(--space-1) var(--space-2)', borderRadius: 999, fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── 3. Location map ──────────────────────────────────────────────── */}
        {hasCoords && (
          <div style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: '0.75rem' }}>Location</h2>
            <div style={{ height: 320, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border)', touchAction: 'none' }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapCenter}
                  zoom={15}
                  options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, zoomControl: true, gestureHandling: 'greedy' }}
                >
                  <MarkerF position={mapCenter} />
                </GoogleMap>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--ink-muted)', fontSize: 'var(--text-base)' }}>
                  Loading map…
                </div>
              )}
            </div>
            {property.address && (
              <p style={{ marginTop: '0.5rem', fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
                {property.address}
              </p>
            )}
          </div>
        )}

        {/* ── 4. Property map image ────────────────────────────────────────── */}
        <div style={{ marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, marginBottom: '0.75rem' }}>Property Map</h2>
          {property.floor_plan_url ? (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--surface-alt)' }}>
              <img
                src={property.floor_plan_url}
                alt={`${property.name} floor plan`}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          ) : (
            <PropertyMapPlaceholder />
          )}
        </div>

        {/* ── 5. Available Stays / For Sale (tabbed, shared scroll area) ─────── */}
        <PropertyListingsTabs
          propertyId={property.id}
          propertyName={property.name}
          rentalListings={listings}
        />

        {/* Guest Photos */}
        <div style={{ marginTop: '2.5rem' }}>
          <GuestPhotos propertyId={property.id} />
        </div>

      </div>

      <Footer />
    </div>
  );
}
